import { useEffect, useState } from 'react';
import { Cpu, Zap, HardDrive, Wifi, AlertTriangle, TrendingUp, Check, DollarSign } from 'lucide-react';
import { billingApi, UsageLimits } from '../services/api';
import clsx from 'clsx';

interface UsageWidgetProps {
  showTitle?: boolean;
  compact?: boolean;
}

export function UsageWidget({ showTitle = true, compact = false }: UsageWidgetProps) {
  const [usageData, setUsageData] = useState<UsageLimits | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await billingApi.getUsage();
      setUsageData(data);
    } catch (err) {
      console.error('Failed to fetch usage data:', err);
      setError('Failed to load usage data');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={clsx('card', compact ? 'p-4' : 'p-6')}>
        <div className="animate-pulse space-y-4">
          {showTitle && <div className="h-5 w-32 skeleton rounded" />}
          <div className="grid grid-cols-2 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 skeleton rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !usageData) {
    return (
      <div className={clsx('card', compact ? 'p-4' : 'p-6')}>
        <div className="text-center py-4">
          <AlertTriangle className="w-8 h-8 text-warning mx-auto mb-2" />
          <p className="text-sm text-foreground-secondary">{error || 'Unable to load usage data'}</p>
        </div>
      </div>
    );
  }

  const usageItems = [
    {
      icon: <Cpu className="w-5 h-5" />,
      label: 'GPU Compute',
      value: usageData.usage.gpuHours.toFixed(1),
      unit: 'hrs',
      cost: usageData.costs.gpuCompute,
      rate: `$${usageData.rates.GPU_COMPUTE}/hr`,
      color: 'accent',
    },
    {
      icon: <Zap className="w-5 h-5" />,
      label: 'API Calls',
      value: usageData.usage.apiCalls.toLocaleString(),
      unit: '',
      cost: usageData.costs.apiCalls,
      rate: `$${usageData.rates.API_CALLS}/call`,
      color: 'success',
    },
    {
      icon: <HardDrive className="w-5 h-5" />,
      label: 'Storage',
      value: usageData.usage.storage.toFixed(1),
      unit: 'GB',
      cost: usageData.costs.storage,
      rate: `$${usageData.rates.STORAGE}/GB`,
      color: 'warning',
    },
    {
      icon: <Wifi className="w-5 h-5" />,
      label: 'Bandwidth',
      value: usageData.usage.bandwidth.toFixed(1),
      unit: 'GB',
      cost: usageData.costs.bandwidth,
      rate: `$${usageData.rates.BANDWIDTH}/GB`,
      color: 'error',
    },
  ];

  return (
    <div className={clsx('card', compact ? 'p-4' : 'p-6')}>
      {showTitle && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-foreground-secondary" />
            <h3 className="font-semibold text-foreground">Current Usage</h3>
          </div>
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-foreground-tertiary" />
            <span className="text-sm font-medium text-foreground tabular-nums">
              ${usageData.totalSpend.toFixed(2)}
            </span>
            {usageData.usageLimitUsd > 0 && (
              <span className="text-xs text-foreground-tertiary">
                / ${usageData.usageLimitUsd.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Spending progress bar */}
      {usageData.usageLimitUsd > 0 && (
        <div className="mb-4">
          <div className="w-full h-2 bg-background-secondary rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full transition-all duration-300 rounded-full',
                usageData.spendPercent >= 95 ? 'bg-error' : usageData.spendPercent >= 80 ? 'bg-warning' : 'bg-accent'
              )}
              style={{ width: `${Math.min(usageData.spendPercent, 100)}%` }}
            />
          </div>
          <p className="text-[10px] text-foreground-tertiary mt-1 tabular-nums">
            {usageData.spendPercent.toFixed(0)}% of monthly limit used
          </p>
        </div>
      )}

      {/* Warnings */}
      {usageData.warnings.length > 0 && (
        <div className="mb-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-medium text-warning mb-1">Warnings</p>
              {usageData.warnings.map((warning, idx) => (
                <p key={idx} className="text-xs text-warning/90">
                  {warning}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Usage Grid */}
      <div className={clsx('grid gap-3', compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4')}>
        {usageItems.map((item) => (
          <UsageCard key={item.label} {...item} compact={compact} />
        ))}
      </div>

      {/* Status */}
      {usageData.withinLimits && usageData.warnings.length === 0 && (
        <div className="mt-4 flex items-center gap-2 text-xs text-success">
          <Check className="w-4 h-4" />
          <span>All usage within limits</span>
        </div>
      )}
    </div>
  );
}

interface UsageCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  unit: string;
  cost: number;
  rate: string;
  color: string;
  compact?: boolean;
}

function UsageCard({ icon, label, value, unit, cost, rate, color, compact }: UsageCardProps) {
  return (
    <div className={clsx('p-3 rounded-lg bg-background-secondary', compact && 'p-2')}>
      <div className={clsx('flex items-center gap-2 mb-2', compact && 'mb-1')}>
        <div
          className={clsx(
            'w-8 h-8 rounded-lg flex items-center justify-center',
            compact && 'w-6 h-6',
            `bg-${color}/10 text-${color}`
          )}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className={clsx('text-xs text-foreground-tertiary truncate', compact && 'text-[10px]')}>{label}</p>
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between">
          <span className={clsx('font-semibold text-foreground tabular-nums', compact ? 'text-sm' : 'text-base')}>
            {value}
            {unit && <span className="text-xs text-foreground-tertiary ml-0.5">{unit}</span>}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-secondary tabular-nums">
            ${cost.toFixed(2)}
          </span>
          {!compact && (
            <span className="text-[10px] text-foreground-tertiary">
              {rate}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
